import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { createPortal } from 'react-dom'

const AddSchoolModal = ({ isOpen, onClose, onSubmit, isLoading, error }) => {
  const { t } = useTranslation()
  const [formData, setFormData] = useState({
    // Objects fields
    name: '',
    description: '',
    latitude: '',
    longitude: '',
    region: '',
    city: '',
    phone: '',
    address: '',
    photo_url: '',
    visible: true,
    additional_url: '',
    // Devices fields
    ip_address: '',
    model: '',
    vendor: '',
    eshdi_vlan_id: '',
    speedtest_target_server: '',
  })

  const [errors, setErrors] = useState({})

  if (!isOpen) return null

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }))
    // Очищаем ошибку при изменении поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const validate = () => {
    const newErrors = {}

    // Обязательные поля
    if (!formData.name.trim()) {
      newErrors.name = t('modals.addSchool.errors.schoolNameRequired')
    }
    if (!formData.ip_address.trim()) {
      newErrors.ip_address = t('modals.addSchool.errors.ipAddressRequired')
    }

    // Валидация IP адреса
    if (formData.ip_address.trim()) {
      const ipRegex = /^(\d{1,3}\.){3}\d{1,3}$/
      if (!ipRegex.test(formData.ip_address.trim())) {
        newErrors.ip_address = t('modals.addSchool.errors.invalidIpFormat')
      }
    }

    // Валидация координат
    if (formData.latitude && (isNaN(parseFloat(formData.latitude)) || parseFloat(formData.latitude) < -90 || parseFloat(formData.latitude) > 90)) {
      newErrors.latitude = t('modals.addSchool.errors.invalidLatitude')
    }
    if (formData.longitude && (isNaN(parseFloat(formData.longitude)) || parseFloat(formData.longitude) < -180 || parseFloat(formData.longitude) > 180)) {
      newErrors.longitude = t('modals.addSchool.errors.invalidLongitude')
    }

    // Валидация VLAN ID
    if (formData.eshdi_vlan_id && (isNaN(parseInt(formData.eshdi_vlan_id, 10)) || parseInt(formData.eshdi_vlan_id, 10) < 0)) {
      newErrors.eshdi_vlan_id = t('modals.addSchool.errors.invalidVlanId')
    }

    // Валидация URL
    if (formData.additional_url && formData.additional_url.trim()) {
      try {
        new URL(formData.additional_url.trim())
      } catch (e) {
        newErrors.additional_url = t('modals.addSchool.errors.invalidUrlFormat')
      }
    }

    if (formData.photo_url && formData.photo_url.trim()) {
      try {
        new URL(formData.photo_url.trim())
      } catch (e) {
        newErrors.photo_url = t('modals.addSchool.errors.invalidUrlFormat')
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()
    if (!validate()) return

    // Подготавливаем данные для отправки
    const submitData = {
      object: {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        region: formData.region.trim() || null,
        city: formData.city.trim() || null,
        phone: formData.phone.trim() || null,
        address: formData.address.trim() || null,
        photo_url: formData.photo_url.trim() || null,
        visible: formData.visible,
        additional_url: formData.additional_url.trim() || null,
      },
      device: {
        ip_address: formData.ip_address.trim(),
        model: formData.model.trim() || null,
        vendor: formData.vendor.trim() || null,
        eshdi_vlan_id: formData.eshdi_vlan_id ? parseInt(formData.eshdi_vlan_id, 10) : null,
        speedtest_target_server: formData.speedtest_target_server?.trim() || null,
      }
    }

    onSubmit(submitData)
  }

  const handleClose = () => {
    setFormData({
      name: '',
      description: '',
      latitude: '',
      longitude: '',
      region: '',
      city: '',
      phone: '',
      address: '',
      photo_url: '',
      visible: true,
      additional_url: '',
      ip_address: '',
      model: '',
      vendor: '',
      eshdi_vlan_id: '',
      speedtest_target_server: '',
    })
    setErrors({})
    onClose()
  }

  const modalContent = (
    <div
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-slate-950/70 px-4 py-10 backdrop-blur-2xl"
      role="dialog"
      aria-modal="true"
      aria-label={t('modals.addSchool.ariaLabel')}
    >
      <div className="pointer-events-none absolute inset-0 bg-brand-radial opacity-50" />

      <div className="relative z-10 w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-3xl border border-surface-border/40 bg-surface-raised/90 shadow-elevated-lg">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-surface-border/60 bg-surface-raised/95 px-6 py-4 backdrop-blur-sm">
          <h2 className="font-display text-2xl text-white">{t('modals.addSchool.title')}</h2>
          <button
            type="button"
            onClick={handleClose}
            disabled={isLoading}
            className="inline-flex items-center justify-center rounded-full p-2 text-muted-400 transition hover:bg-surface-base/60 hover:text-white disabled:opacity-50"
          >
            <span className="material-icons">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 rounded-lg border border-critical/50 bg-critical/10 px-4 py-3 text-sm text-critical">
              {error}
            </div>
          )}
          <div className="space-y-6">
            {/* Основная информация */}
            <section>
              <h3 className="mb-4 text-lg font-semibold text-white">{t('modals.addSchool.basicInfo')}</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-muted-300">
                    {t('modals.addSchool.schoolName')} <span className="text-critical">{t('modals.addSchool.required')}</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.name ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.schoolNamePlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.name && <p className="mt-1 text-xs text-critical">{errors.name}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.description')}</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows={3}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.descriptionPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </section>

            {/* Устройство */}
            <section>
              <h3 className="mb-4 text-lg font-semibold text-white">{t('modals.addSchool.device')}</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">
                    {t('modals.addSchool.ipAddress')} <span className="text-critical">{t('modals.addSchool.required')}</span>
                  </label>
                  <input
                    type="text"
                    name="ip_address"
                    value={formData.ip_address}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.ip_address ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.ipAddressPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.ip_address && <p className="mt-1 text-xs text-critical">{errors.ip_address}</p>}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.speedtestTargetServer')}</label>
                  <input
                    type="text"
                    name="speedtest_target_server"
                    value={formData.speedtest_target_server}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.speedtestTargetServerPlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.vlanId')}</label>
                  <input
                    type="number"
                    name="eshdi_vlan_id"
                    value={formData.eshdi_vlan_id}
                    onChange={handleChange}
                    min="0"
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.eshdi_vlan_id ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.vlanIdPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.eshdi_vlan_id && <p className="mt-1 text-xs text-critical">{errors.eshdi_vlan_id}</p>}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.model')}</label>
                  <input
                    type="text"
                    name="model"
                    value={formData.model}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.modelPlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.vendor')}</label>
                  <input
                    type="text"
                    name="vendor"
                    value={formData.vendor}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.vendorPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </section>

            {/* Местоположение */}
            <section>
              <h3 className="mb-4 text-lg font-semibold text-white">{t('modals.addSchool.location')}</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.region')}</label>
                  <input
                    type="text"
                    name="region"
                    value={formData.region}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.regionPlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.city')}</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.cityPlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.latitude')}</label>
                  <input
                    type="number"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    step="any"
                    min="-90"
                    max="90"
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.latitude ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.latitudePlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.latitude && <p className="mt-1 text-xs text-critical">{errors.latitude}</p>}
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.longitude')}</label>
                  <input
                    type="number"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    step="any"
                    min="-180"
                    max="180"
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.longitude ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.longitudePlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.longitude && <p className="mt-1 text-xs text-critical">{errors.longitude}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.address')}</label>
                  <input
                    type="text"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.addressPlaceholder')}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </section>

            {/* Контакты и дополнительные данные */}
            <section>
              <h3 className="mb-4 text-lg font-semibold text-white">{t('modals.addSchool.contacts')}</h3>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.phone')}</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full rounded-lg border border-surface-border/60 bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 focus:ring-brand-400/50"
                    placeholder={t('modals.addSchool.phonePlaceholder')}
                    disabled={isLoading}
                  />
                </div>

                <div>
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.photoUrl')}</label>
                  <input
                    type="url"
                    name="photo_url"
                    value={formData.photo_url}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.photo_url ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.photoUrlPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.photo_url && <p className="mt-1 text-xs text-critical">{errors.photo_url}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="mb-2 block text-sm font-semibold text-muted-300">{t('modals.addSchool.additionalUrl')}</label>
                  <input
                    type="url"
                    name="additional_url"
                    value={formData.additional_url}
                    onChange={handleChange}
                    className={`w-full rounded-lg border bg-surface-base/80 px-3 py-2 text-sm text-white placeholder:text-muted-500 focus:outline-none focus:ring-2 ${
                      errors.additional_url ? 'border-critical/50 focus:ring-critical/50' : 'border-surface-border/60 focus:ring-brand-400/50'
                    }`}
                    placeholder={t('modals.addSchool.additionalUrlPlaceholder')}
                    disabled={isLoading}
                  />
                  {errors.additional_url && <p className="mt-1 text-xs text-critical">{errors.additional_url}</p>}
                </div>

                <div className="md:col-span-2">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      name="visible"
                      checked={formData.visible}
                      onChange={handleChange}
                      className="h-4 w-4 rounded border-surface-border/60 bg-surface-base/80 text-brand-400 focus:ring-2 focus:ring-brand-400/50"
                      disabled={isLoading}
                    />
                    <span className="text-sm font-semibold text-muted-300">{t('modals.addSchool.visible')}</span>
                  </label>
                </div>
              </div>
            </section>
          </div>

          <div className="mt-6 flex items-center justify-end gap-3 border-t border-surface-border/60 pt-6">
            <button
              type="button"
              onClick={handleClose}
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-surface-base/60 px-4 py-2 text-sm font-semibold text-muted-300 transition hover:bg-surface-base/80 hover:text-white disabled:opacity-50"
            >
              {t('modals.addSchool.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="inline-flex items-center gap-2 rounded-xl bg-brand-400/20 px-4 py-2 text-sm font-semibold text-brand-100 transition hover:bg-brand-400/30 hover:text-white disabled:opacity-50"
            >
              {isLoading ? (
                <>
                  <span className="material-icons text-sm animate-spin">refresh</span>
                  {t('modals.addSchool.creating')}
                </>
              ) : (
                <>
                  <span className="material-icons text-sm">add</span>
                  {t('modals.addSchool.createSchool')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  )

  return typeof document !== 'undefined' ? createPortal(modalContent, document.body) : null
}

export default AddSchoolModal

